import heic2any from 'heic2any';

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  outputFormat?: 'jpeg' | 'webp';
}

// HEIC to JPEG conversion function (same as ItemCard implementation)
export const convertHeicToJpeg = async (file: File): Promise<File> => {
  try {
    // Check if it's a HEIC file
    if (file.type === 'image/heic' || file.type === 'image/heif' || 
        file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')) {
      
      console.log('Converting HEIC file to JPEG...');
      const convertedBlob = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.9
      }) as Blob;
      
      const convertedFile = new File([convertedBlob], file.name.replace(/\.heic?$/i, '.jpg'), {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
      
      console.log('HEIC conversion successful');
      return convertedFile;
    }
    
    // Not a HEIC file, return as-is
    return file;
  } catch (error) {
    console.warn('HEIC conversion failed, trying to use original file:', error);
    return file;
  }
};

/**
 * Image compression utility (same as ItemCard implementation) 
 */
export const compressImage = (file: File, maxWidth: number = 1200, quality: number = 0.8): Promise<File> => {
  return new Promise((resolve, reject) => {
    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      resolve(file); // Return original if not an image
      return;
    }
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      try {
        // Calculate new dimensions
        let { width, height } = img;
        
        // Only compress if image is larger than maxWidth
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx?.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              resolve(file); // Fallback to original file
            }
          },
          'image/jpeg',
          quality
        );
      } catch (error) {
        console.warn('Image compression failed, using original file:', error);
        resolve(file);
      }
    };
    
    img.onerror = () => {
      console.warn('Failed to load image for compression, using original file');
      resolve(file);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Complete image processing pipeline (HEIC conversion + compression)
 */
export const processImageFile = async (file: File, maxWidth: number = 800, quality: number = 0.8): Promise<File> => {
  try {
    // Step 1: Convert HEIC to JPEG if needed
    let processedFile = await convertHeicToJpeg(file);
    
    // Step 2: Compress the image
    processedFile = await compressImage(processedFile, maxWidth, quality);
    
    console.log(`File processing complete: ${file.size} → ${processedFile.size} bytes (${Math.round((1 - processedFile.size / file.size) * 100)}% reduction)`);
    
    return processedFile;
  } catch (error) {
    console.error('Image processing error:', error);
    throw error;
  }
};



/**
 * Validates if a file is a supported image format including HEIC
 */
export const isValidImageFile = (file: File): boolean => {
  const supportedTypes = [
    'image/jpeg',
    'image/jpg', 
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif'
  ];

  const supportedExtensions = [
    '.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'
  ];

  const hasValidType = supportedTypes.includes(file.type.toLowerCase());
  const hasValidExtension = supportedExtensions.some(ext => 
    file.name.toLowerCase().endsWith(ext)
  );

  return hasValidType || hasValidExtension;
};

/**
 * Gets a user-friendly error message for unsupported files
 */
export const getImageValidationError = (file: File): string | null => {
  if (!isValidImageFile(file)) {
    return 'Please select a valid image file (JPEG, PNG, WebP, or HEIC format)';
  }

  // Check file size (10MB limit)
  const maxSizeBytes = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSizeBytes) {
    return 'Image file is too large. Please select an image smaller than 10MB.';
  }

  return null;
};
